import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { RouteObject, createBrowserRouter } from 'react-router-dom';

import AuthLayout from '@/components/auth/AuthLayout';
import RequireAuth from '@/components/auth/RequireAuth';
// import BillingDashboard from '@/components/billing/BillingDashboard';
import { chatLoader } from '@/components/chat/utils/chat-loader';
import { ERROR_INVALID_PRODUCT_ID } from '@/components/chat/utils/error-messages';
import ConsumerDashboardLayout from '@/components/consumer/ConsumerDashboardLayout';
import AdminGuard from '@/components/dashboard/AdminGuard';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import HaggleEventDashboard from '@/components/dashboard/haggle-event/HaggleEventDashboard';
import HaggleEventMonthReportDashboard from '@/components/dashboard/haggle-event/HaggleEventMonthReportDashboard';
import HaggleSingleEventDashboard from '@/components/dashboard/haggle-event/HaggleSingleEventDashboard';
import OrderDashboard from '@/components/dashboard/order/OrderDashboard';
import OrderViewPage from '@/components/dashboard/order/OrderView';
import DashboardLayoutPublic from '@/components/dashboard/public/DashboardLayoutPublic';
import StaticPageLayout from '@/components/dashboard/public/StaticPageLayout';
import StoreMerchantsDashboard from '@/components/dashboard/store/StoreMerchantsDashboard';
import StoreProductsDashboard from '@/components/dashboard/store/StoreProductsDashboard';
import SupportDashboard from '@/components/support/SupportDashboard';
import ErrorElement from '@/components/ui/error/ErrorElement';
import { AppRoutes } from '@/interfaces/routes/AppRoutes';
import Dashboard from '@/pages/Admin/Dashboard';
import ConfgiureApp from '@/pages/Admin/configApp/configApp';
import AdminHagglesDashboard from '@/pages/Admin/haggles/AdminHagglesDashboard';
import InvitedUsers from '@/pages/Admin/invitedUsers/InvitedUsers';
import AdminProductsDashboard from '@/pages/Admin/products/AdminProductsDashboard';
import Prompts from '@/pages/Admin/prompts/Prompts';
import AdminReportsDashboard from '@/pages/Admin/reports/AdminReportsDashboard';
import ShopifySubscriptionDiscounts from '@/pages/Admin/shopify-subscription-discounts/ShopifySubscriptionDiscounts';
import StoreDetails from '@/pages/Admin/store/StoreDetails';
import Stores from '@/pages/Admin/store/Stores';
import UserDetail from '@/pages/Admin/user/UserDetail';
import Users from '@/pages/Admin/user/Users';
import HaggleInvitedUsers from '@/pages/HaggleInvitedUsers/HaggleInvitedUsers';
import HowToVideos from '@/pages/HowToVideos/HowToVideos';
import PrivateB2BMarketplace from '@/pages/Privateb2bMarketplace/PrivateB2BMarketplace';
import ConfirmInvite from '@/pages/auth/ConfirmInvite';
import ConfirmPassword from '@/pages/auth/ConfirmPassword';
import ConfirmUser from '@/pages/auth/ConfirmUser';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import ResendConfirmationCode from '@/pages/auth/ResendConfirmationCode';
import ShopifyOauth from '@/pages/auth/ShopifyOAuth';
import SignIn from '@/pages/auth/SignIn';
import SignInConsumer from '@/pages/auth/SignInConsumer';
import SignOut from '@/pages/auth/SignOut';
import SignUp from '@/pages/auth/SignUp';
import SignUpConsumer from '@/pages/auth/SignUpConsumer';
import B2BLiveVendors from '@/pages/b2bLiveVendors/b2bLiveVendors';
import B2BMarketplace from '@/pages/b2bMarketplace/B2BMarketplace';
import ChatPage from '@/pages/chat/ChatPage';
import AddProfile from '@/pages/consumer/AddProfile';
import Notifications from '@/pages/consumer/Notifications';
import Profile from '@/pages/consumer/Profile';
import CustomizeChatbot from '@/pages/customizedChatbot/CustomizedChatbot';
import Home from '@/pages/home/Home';
import InventoryProductDetail from '@/pages/inventoryProductDetail/InventoryProductDetail';
import LocalCheckout from '@/pages/localCheckout/localCheckout';
import ShopifyProductDetail from '@/pages/shopifyProductDetail/ShopifyProductDetail';
import StorePage from '@/pages/store/StorePage';
import Store from '@/pages/user/Store';
import SettingsDashboard from '@/settings/SettingsDashboard';

import Root from '@pages/Root';
import About from '@pages/about/About';
import Landing from '@pages/static-landing-pages/Landing';
import Merchants from '@pages/static-landing-pages/Merchants';
import ThankYou from '@pages/static-landing-pages/ThankYou';
import Welcome from '@pages/static-landing-pages/Welcome';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY ?? '');

const authentication: RouteObject[] = [
	{
		path: 'sign-in',
		element: <SignIn />,
	},
	{
		path: 'customer-sign-in',
		element: <SignInConsumer />,
	},
	{
		path: 'sign-up',
		element: <SignUp />,
	},
	{
		path: 'customer-sign-up',
		element: <SignUpConsumer />,
	},
	{
		path: 'confirm-user',
		element: <ConfirmUser />,
	},
	{
		path: 'confirm-password',
		element: <ConfirmPassword />,
	},
	{
		path: 'confirm-invite',
		element: <ConfirmInvite />,
	},
	{
		path: 'resend-confirmation-code',
		element: <ResendConfirmationCode />,
	},
	{
		path: 'forgot-password',
		element: <ForgotPassword />,
	},
	{
		path: 'sign-out',
		element: <SignOut />,
	},
];

const aboutUs: RouteObject[] = [
	{
		path: 'about',
		element: (
			<RequireAuth>
				<About />,
			</RequireAuth>
		),
	},
];

const admin: RouteObject[] = [
	{
		element: <AdminGuard />,
		children: [
			{
				path: 'merchants',
				element: <Stores />,
			},
			{
				path: 'haggles',
				element: <AdminHagglesDashboard />,
			},
			{
				path: 'prompts',
				element: <Prompts />,
			},
			{
				path: 'shopify-subscription-discounts',
				element: <ShopifySubscriptionDiscounts />,
			},
			{
				path: 'products',
				element: <AdminProductsDashboard />,
			},
			{
				path: 'reports',
				element: <AdminReportsDashboard />,
			},
			{
				path: 'reports/:date',
				element: <AdminReportsDashboard />,
			},
			{
				path: 'users',
				element: <Users />,
			},

			{
				path: 'invited',
				element: <InvitedUsers />,
			},
			{
				path: 'configure-app',
				element: <ConfgiureApp />,
			},
			{
				path: 'merchants/:storeTradeName',
				element: <StoreDetails />,
			},
			{
				path: 'users/:id',
				element: <UserDetail />,
			},
		],
	},
];

const user: RouteObject[] = [
	{
		path: 'dashboard',
		element: (
			<Elements stripe={stripePromise}>
				<Dashboard />
			</Elements>
		),
	},
	{
		path: 'dashboard/haggle-events',
		element: <HaggleEventDashboard />,
	},
	{
		path: 'dashboard/haggle-events/:id',
		element: <HaggleSingleEventDashboard />,
	},

	{
		path: 'dashboard/users',
		element: <StoreMerchantsDashboard />,
	},
	{
		path: 'dashboard/haggle-invited',
		element: <HaggleInvitedUsers />,
	},
	{
		path: 'dashboard/products',
		element: <StoreProductsDashboard />,
	},
	{
		path: 'dashboard/reports',
		element: <HaggleEventMonthReportDashboard />,
	},
	{
		path: 'dashboard/reports/:date',
		element: <HaggleEventMonthReportDashboard />,
	},
	{
		path: 'dashboard/how-to-videos',
		element: <HowToVideos />,
	},
	// {
	// 	path: 'billing',
	// 	element: <BillingDashboard />,
	// },
	{
		path: 'support',
		element: <SupportDashboard />,
	},
	{
		path: 'settings',
		element: <SettingsDashboard />,
	},
	{
		path: 'dashboard/orders',
		element: <OrderDashboard />,
	},
	{
		path: 'dashboard/orders/:id',
		element: <OrderViewPage />,
	},
	{
		path: 'customize-chatbot',
		element: <CustomizeChatbot />,
	},
	{
		path: 'b2b-marketplace',
		element: <B2BMarketplace />,
	},
	{
		path: 'b2b-vendors',
		element: <B2BLiveVendors />,
	},
];

const consumer: RouteObject[] = [
	{
		path: 'profile',
		element: <Profile />,
	},
	{
		path: 'profile/add',
		element: <AddProfile />,
	},
	{
		path: 'notifications',
		element: <Notifications />,
	},
	{
		path: 'orders',
		element: <OrderDashboard />,
	},
	{
		path: 'orders/:id',
		element: <OrderViewPage />,
	},
];

const router = createBrowserRouter([
	{
		element: <StaticPageLayout />,
		children: [
			{
				path: '/welcome',
				element: <Welcome />,
			},
			{
				path: '/thank-you',
				element: <ThankYou />,
			},
			{
				path: '/merchants',
				element: <Merchants />,
			},
			{
				path: '/landing',
				element: <Landing />,
			},
		],
	},
	{
		element: <DashboardLayoutPublic />,
		children: [
			{
				index: true,
				path: '/',
				element: <Home />,
			},
			{
				path: '/:storeName/product-view/:productId',
				element: <ShopifyProductDetail />,
			},
			{
				path: '/:storeName/product-detail/:productId',
				element: <InventoryProductDetail />,
			},
			{
				path: '/checkout',
				element: <LocalCheckout />,
			},
			{
				path: 'private-b2b-marketplace/:merchantId/:invitedUserEmail/:haggleEventId',
				element: <PrivateB2BMarketplace />,
			},
		],
	},
	{
		path: '/',
		element: <Root />,
		children: [
			{
				path: '/user/manage/store/',
				element: (
					<RequireAuth>
						<Store />
					</RequireAuth>
				),
			},
			{
				path: AppRoutes.ShopifyOAuth,
				element: (
					<RequireAuth>
						<ShopifyOauth />
					</RequireAuth>
				),
			},
			{
				path: 'auth/',
				element: <AuthLayout />,
				children: authentication,
			},
			{
				path: 'us/',
				children: aboutUs,
			},
			{
				path: 'store/:storeName',
				element: <StorePage />,
			},
		],
	},
	{
		path: 'user/',
		element: <DashboardLayout />,
		children: [...user, ...admin],
	},
	{
		path: 'customer/',
		element: <ConsumerDashboardLayout />,
		children: consumer,
	},
	{
		path: 'chat/store/product/:productId/variant/:variantId',
		element: <ChatPage />,
		loader: chatLoader,
		errorElement: <ErrorElement message={ERROR_INVALID_PRODUCT_ID} />,
	},
]);

export default router;
